import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import client from "../sanity/client";
import useIntersectionObserver from "../hooks/useIntersectionObserver";
import EventCard from "../components/subComponents/eventCard";
import Contact from "../components/Contact";
import { ring } from "ldrs";

const EVENTS_QUERY = `*[_type == "event" && defined(date) && status != "draft"] {
	_id,
	name,
	date,
	venue->{ name },
	"image": image.asset->url
}`;

const PAST_EVENTS_PREVIEW_COUNT = 4;

const Events = () => {
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showAllPast, setShowAllPast] = useState(false);

	const sectionsRef = useIntersectionObserver(
		(target) => target.classList.add("visible"),
		{ threshold: 0.1 },
	);

	ring.register();

	useEffect(() => {
		const fetchEvents = async () => {
			try {
				const data = await client.fetch(EVENTS_QUERY);
				setEvents(data);
			} catch (error) {
				console.error("Error fetching events:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchEvents();
	}, []);

	const { upcoming, past } = useMemo(() => {
		const now = Date.now();
		const upcomingEvents = events
			.filter((event) => new Date(event.date).getTime() >= now)
			.sort((a, b) => new Date(a.date) - new Date(b.date));
		const pastEvents = events
			.filter((event) => new Date(event.date).getTime() < now)
			.sort((a, b) => new Date(b.date) - new Date(a.date));

		return { upcoming: upcomingEvents, past: pastEvents };
	}, [events]);

	if (loading) {
		return (
			<div className="loadingAnimationContainer">
				<l-ring size="40" stroke="5" bg-opacity="0" speed="2" color="black"></l-ring>
			</div>
		);
	}

	return (
		<section className="allEventsContainer">
			<div
				ref={(el) => (sectionsRef.current[0] = el)}
				className="eventsPageHeader animate"
			>
				<h1 className="eventsPageTitle">Events</h1>
				<p className="eventsPageSubtitle">
					Gatherings, celebrations, and community nights, past and upcoming.
				</p>
			</div>

			{events.length === 0 ? (
				<div
					ref={(el) => (sectionsRef.current[1] = el)}
					className="eventsEmptyState animate"
				>
					<h2 className="eventsEmptyStateTitle">Nothing on the calendar yet</h2>
					<p className="eventsEmptyStateText">
						Check back soon. New gatherings are added as they're planned.
					</p>
					<Link to="/" className="btn btnGhostDark">
						Back to home
					</Link>
				</div>
			) : (
				<>
					<section
						ref={(el) => (sectionsRef.current[1] = el)}
						className="eventsPageSection animate"
					>
						<h2 className="eventsPageSectionHeading">Upcoming</h2>
						{upcoming.length > 0 ? (
							<div className="eventsPageGrid">
								{upcoming.map((event) => (
									<EventCard event={event} key={event._id} />
								))}
							</div>
						) : (
							<p className="eventsPageEmptyText">
								Nothing on the calendar right now. Check back soon.
							</p>
						)}
					</section>

					{past.length > 0 && (
						<section
							ref={(el) => (sectionsRef.current[2] = el)}
							className="eventsPageSection animate"
						>
							<h2 className="eventsPageSectionHeading">Past events</h2>
							<div className="eventsPageGrid">
								{(showAllPast
									? past
									: past.slice(0, PAST_EVENTS_PREVIEW_COUNT)
								).map((event) => (
									<EventCard event={event} key={event._id} />
								))}
							</div>
							{!showAllPast && past.length > PAST_EVENTS_PREVIEW_COUNT && (
								<button
									className="btn btnGhostDark eventsShowMoreBtn"
									onClick={() => setShowAllPast(true)}
								>
									Show more
								</button>
							)}
						</section>
					)}
				</>
			)}

			<Contact />
		</section>
	);
};

export default Events;
